import type { FC, ReactNode } from 'react'
import type { ClinicSettings, Patient, Prescription } from '@/lib/clinic/types'

import { useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useClinic } from '@/hooks/use-clinic'
import { useClinicUi } from '@/hooks/use-clinic-ui'
import { useUnsavedChangesWarning } from '@/hooks/use-form-draft'
import { formatDate } from '@/lib/clinic/datetime'
import { pluralize } from '@/lib/clinic/format'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Seo } from '@/components/generals/seo'
import Panel from '@/components/generals/panel'
import PageHeader from '@/components/generals/page-header'
import PageLoader from '@/components/generals/page-loader'
import PageContainer from '@/components/generals/page-container'
import Field from '@/components/common/field'
import SegmentedControl from '@/components/common/segmented-control'
import LogoField from '@/components/settings/logo-field'
import ListEditor from '@/components/settings/list-editor'
import TemplateEditor from '@/components/settings/template-editor'
import A4Preview from '@/components/prescriptions/a4-preview'
import PrescriptionSheet from '@/components/prescriptions/prescription-sheet'

import {
    Building2,
    Database,
    FileText,
    HardDrive,
    LayoutTemplate,
    ListChecks,
    MessageCircle,
    RotateCcw,
    Save,
    Signature,
    Trash2
} from 'lucide-react'

import {
    DEFAULT_COMPLAINTS,
    DEFAULT_MEDICINE_INSTRUCTIONS,
    DEFAULT_TREATMENTS
} from '@/constants/clinic'

type SettingsTab = 'clinic' | 'prescription' | 'lists' | 'whatsapp' | 'data'

const TABS: Array<{ value: SettingsTab, label: string }> = [
    { value: 'clinic', label: 'Clinic details' },
    { value: 'prescription', label: 'Prescription' },
    { value: 'lists', label: 'Custom lists' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'data', label: 'Data' }
]

const isSettingsTab = (value: unknown): value is SettingsTab => typeof value === 'string' && TABS.some(tab => tab.value === value)

const FIELD_TABS: Record<string, SettingsTab> = {
    clinicName: 'clinic',
    doctorName: 'clinic',
    letterheadSpace: 'prescription',
    countryCode: 'whatsapp'
}

const SAMPLE_PATIENT: Patient = {
    id: 'sample-patient',
    code: 'DSC-0000',
    name: 'Sample Patient',
    phone: '9000000000',
    age: 35,
    dateOfBirth: null,
    gender: 'female',
    place: 'Kochi',
    address: '',
    medicalHistory: '',
    allergies: '',
    notes: '',
    createdAt: '2026-01-01T04:30:00.000Z',
    updatedAt: '2026-01-01T04:30:00.000Z',
    history: []
}

const SAMPLE_PRESCRIPTION: Prescription = {
    id: 'sample-prescription',
    number: 'RX-0000',
    patientId: 'sample-patient',
    consultationId: null,
    prescribedAt: '2026-01-01T05:00:00.000Z',
    complaint: 'Tooth pain',
    diagnosis: 'Symptomatic irreversible pulpitis i.r.t 36',
    medicines: [
        { id: 'm1', name: 'Amoxicillin 500 mg', dosage: '1 capsule', frequency: '1-1-1', duration: '5 days', instructions: 'After food' },
        { id: 'm2', name: 'Aceclofenac 100 mg + Paracetamol 325 mg', dosage: '1 tablet', frequency: '1-0-1', duration: '3 days', instructions: 'After food' }
    ],
    advice: '',
    notes: '',
    createdAt: '2026-01-01T05:00:00.000Z',
    updatedAt: '2026-01-01T05:00:00.000Z',
    history: []
}

const validateSettings = (settings: ClinicSettings): Record<string, string> => {
    const errors: Record<string, string> = {}

    if (!settings.clinicName.trim()) errors.clinicName = 'Enter the clinic name.'
    if (!settings.doctorName.trim()) errors.doctorName = 'Enter the doctor name.'
    if (!/^\d{1,4}$/.test(settings.countryCode.trim())) errors.countryCode = 'Enter 1 to 4 digits, for example 91.'

    if (settings.printHeader === 'letterhead' && (settings.letterheadSpace < 10 || settings.letterheadSpace > 100)) {
        errors.letterheadSpace = 'Enter a value between 10 and 100 mm.'
    }

    settings.templates.forEach(template => {
        if (!template.name.trim()) errors[`${template.id}-name`] = 'Give this template a name.'
        if (!template.body.trim()) errors[`${template.id}-body`] = 'Write the message text.'
    })

    return errors
}

interface ToggleRowProps {
    id: string
    label: string
    description: string
    checked: boolean
    onChange: (checked: boolean) => void
}

const ToggleRow: FC<ToggleRowProps> = ({ id, label, description, checked, onChange }): ReactNode => {
    return (
        <div className='flex items-start justify-between gap-4 py-3'>
            <div className='flex flex-col gap-0.5'>
                <label htmlFor={id} className='text-sm font-medium text-foreground'>
                    {label}
                </label>

                <p className='text-xs text-muted-foreground'>
                    {description}
                </p>
            </div>

            <Switch id={id} checked={checked} onCheckedChange={value => onChange(Boolean(value))} />
        </div>
    )
}

const SettingsPage: FC = (): ReactNode => {
    const router = useRouter()
    const { ready, data, index, actions, today } = useClinic()
    const ui = useClinicUi()
    const [draft, setDraft] = useState<ClinicSettings | null>(null)
    const [errors, setErrors] = useState<Record<string, string>>({})

    const settings = draft ?? data.settings
    const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(data.settings)

    useUnsavedChangesWarning(dirty)

    const sample = useMemo(() => {
        const latest = [...data.prescriptions].sort((a, b) => b.prescribedAt.localeCompare(a.prescribedAt))[0]
        const patient = latest ? index.patientsById.get(latest.patientId) : undefined

        if (latest && patient) {
            return {
                prescription: latest,
                patient,
                consultation: latest.consultationId ? index.consultationsById.get(latest.consultationId) ?? null : null
            }
        }

        return { prescription: SAMPLE_PRESCRIPTION, patient: SAMPLE_PATIENT, consultation: null }
    }, [data.prescriptions, index.consultationsById, index.patientsById])

    if (!ready || !router.isReady) return <PageLoader />

    const tab: SettingsTab = isSettingsTab(router.query.tab) ? router.query.tab : 'clinic'

    const changeTab = (value: SettingsTab) => {
        void router.replace({ pathname: '/settings', query: { tab: value } }, undefined, { shallow: true, scroll: false })
    }

    const update = <K extends keyof ClinicSettings>(key: K, value: ClinicSettings[K]) => {
        setDraft(current => ({ ...(current ?? data.settings), [key]: value }))

        if (errors[key as string]) {
            setErrors(current => {
                const next = { ...current }
                delete next[key as string]

                return next
            })
        }
    }

    const save = () => {
        if (!draft) return

        const nextErrors = validateSettings(draft)
        setErrors(nextErrors)

        const firstError = Object.keys(nextErrors)[0]

        if (firstError) {
            changeTab(FIELD_TABS[firstError] ?? 'whatsapp')
            toast.error('Some settings need attention before saving.')
            return
        }

        actions.updateSettings({
            ...draft,
            clinicName: draft.clinicName.trim(),
            doctorName: draft.doctorName.trim(),
            countryCode: draft.countryCode.trim(),
            templates: draft.templates.map(template => ({ ...template, name: template.name.trim(), body: template.body.trim() }))
        })

        setDraft(null)
        toast.success('Settings saved')
    }

    const discard = () => {
        setDraft(null)
        setErrors({})
    }

    const sampleValues = {
        patientName: 'Anjali Kumar',
        clinicName: settings.clinicName,
        doctorName: settings.doctorName,
        followUpDate: formatDate(today),
        treatment: 'Root Canal Treatment',
        clinicPhone: settings.phone
    }

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    const counts = [
        { label: 'Patients', value: data.patients.length },
        { label: 'Consultations', value: data.consultations.length },
        { label: 'Prescriptions', value: data.prescriptions.length },
        { label: 'Follow-ups', value: data.followUps.length }
    ]

    return (
        <>
            <Seo title='Settings' description='Clinic details, prescription layout, custom lists and WhatsApp templates.' />

            <PageContainer className={cn(dirty && 'pb-28')}>
                <PageHeader
                    title='Settings'
                    description='Clinic details, prescription layout, quick-pick lists and WhatsApp messages.'
                />

                <Tabs value={tab} onValueChange={value => changeTab(value as SettingsTab)} className='gap-6'>
                    <div className='-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0'>
                        <TabsList className='h-10 w-max'>
                            {TABS.map(item => (
                                <TabsTrigger key={item.value} value={item.value} className='px-3'>
                                    {item.label}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </div>

                    <TabsContent value='clinic'>
                        <Panel title='Clinic details' description='Shown in the app and printed on every prescription.' icon={Building2} contentClassName='p-5'>
                            <div className='grid gap-5 md:grid-cols-2'>
                                <Field label='Clinic logo' className='md:col-span-2'>
                                    <LogoField value={settings.logo} onChange={value => update('logo', value)} />
                                </Field>

                                <Field label='Clinic name' htmlFor='settings-clinic-name' required error={errors.clinicName}>
                                    <Input
                                        id='settings-clinic-name'
                                        value={settings.clinicName}
                                        onChange={event => update('clinicName', event.target.value)}
                                        aria-invalid={Boolean(errors.clinicName) || undefined}
                                    />
                                </Field>

                                <Field label='Contact number' htmlFor='settings-phone'>
                                    <Input
                                        id='settings-phone'
                                        type='tel'
                                        value={settings.phone}
                                        onChange={event => update('phone', event.target.value)}
                                    />
                                </Field>

                                <Field label='Doctor name' htmlFor='settings-doctor' required error={errors.doctorName}>
                                    <Input
                                        id='settings-doctor'
                                        value={settings.doctorName}
                                        onChange={event => update('doctorName', event.target.value)}
                                        aria-invalid={Boolean(errors.doctorName) || undefined}
                                    />
                                </Field>

                                <Field label='Doctor qualification' htmlFor='settings-qualification'>
                                    <Input
                                        id='settings-qualification'
                                        value={settings.qualification}
                                        onChange={event => update('qualification', event.target.value)}
                                        placeholder='e.g. BDS, MDS'
                                    />
                                </Field>

                                <Field label='Clinic address' htmlFor='settings-address' className='md:col-span-2'>
                                    <Textarea
                                        id='settings-address'
                                        rows={2}
                                        value={settings.address}
                                        onChange={event => update('address', event.target.value)}
                                    />
                                </Field>
                            </div>
                        </Panel>
                    </TabsContent>

                    <TabsContent value='prescription'>
                        <div className='grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]'>
                            <div className='flex min-w-0 flex-col gap-6'>
                                <Panel title='Prescription content' icon={FileText} contentClassName='flex flex-col gap-5 p-5'>
                                    <Field label='Default instructions' htmlFor='settings-advice' hint='Filled into the advice of every new prescription. One instruction per line.'>
                                        <Textarea
                                            id='settings-advice'
                                            rows={5}
                                            value={settings.defaultAdvice}
                                            onChange={event => update('defaultAdvice', event.target.value)}
                                        />
                                    </Field>

                                    <Field label='Prescription footer' htmlFor='settings-footer' hint='Printed at the bottom of every prescription.'>
                                        <Textarea
                                            id='settings-footer'
                                            rows={2}
                                            value={settings.footer}
                                            onChange={event => update('footer', event.target.value)}
                                        />
                                    </Field>
                                </Panel>

                                <Panel title='Doctor signature' icon={Signature} contentClassName='grid gap-5 p-5 md:grid-cols-2'>
                                    <Field label='Name under the signature' htmlFor='settings-signature-name'>
                                        <Input
                                            id='settings-signature-name'
                                            value={settings.signatureName}
                                            onChange={event => update('signatureName', event.target.value)}
                                        />
                                    </Field>

                                    <Field label='Signature details' htmlFor='settings-signature-details' hint='For example qualification or registration number.'>
                                        <Input
                                            id='settings-signature-details'
                                            value={settings.signatureDetails}
                                            onChange={event => update('signatureDetails', event.target.value)}
                                        />
                                    </Field>
                                </Panel>

                                <Panel title='Print layout' icon={LayoutTemplate} contentClassName='flex flex-col gap-4 p-5'>
                                    <Field label='Page header' htmlFor='settings-header'>
                                        <SegmentedControl
                                            id='settings-header'
                                            options={[
                                                { value: 'full', label: 'Print clinic header' },
                                                { value: 'letterhead', label: 'Pre-printed letterhead' }
                                            ]}
                                            value={settings.printHeader}
                                            onChange={value => update('printHeader', value)}
                                            ariaLabel='Page header'
                                            className='max-w-md'
                                        />
                                    </Field>

                                    {settings.printHeader === 'letterhead' && (
                                        <Field
                                            label='Space left for the letterhead'
                                            htmlFor='settings-letterhead'
                                            hint='Blank space at the top of the page, in millimetres.'
                                            error={errors.letterheadSpace}
                                        >
                                            <div className='relative max-w-[180px]'>
                                                <Input
                                                    id='settings-letterhead'
                                                    type='number'
                                                    min={10}
                                                    max={100}
                                                    value={settings.letterheadSpace}
                                                    onChange={event => update('letterheadSpace', Number(event.target.value))}
                                                    className='pr-12'
                                                    aria-invalid={Boolean(errors.letterheadSpace) || undefined}
                                                />

                                                <span className='pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground'>
                                                    mm
                                                </span>
                                            </div>
                                        </Field>
                                    )}

                                    <Field label='Text size' htmlFor='settings-text-size'>
                                        <SegmentedControl
                                            id='settings-text-size'
                                            options={[
                                                { value: 'standard', label: 'Standard' },
                                                { value: 'large', label: 'Large' }
                                            ]}
                                            value={settings.printTextSize}
                                            onChange={value => update('printTextSize', value)}
                                            ariaLabel='Text size'
                                            className='max-w-xs'
                                        />
                                    </Field>

                                    <div className='divide-y divide-border border-t border-border'>
                                        <ToggleRow
                                            id='settings-show-logo'
                                            label='Show clinic logo'
                                            description='Prints the logo next to the clinic name when a logo is uploaded.'
                                            checked={settings.showLogo}
                                            onChange={value => update('showLogo', value)}
                                        />

                                        <ToggleRow
                                            id='settings-show-complaint'
                                            label='Show chief complaint'
                                            description='Prints the chief complaint above the diagnosis.'
                                            checked={settings.showComplaint}
                                            onChange={value => update('showComplaint', value)}
                                        />
                                    </div>
                                </Panel>
                            </div>

                            <aside className='flex flex-col gap-2 xl:sticky xl:top-6 xl:self-start'>
                                <p className='text-[13px] font-medium text-foreground'>
                                    Live preview
                                </p>

                                <div className='rounded-2xl border border-border bg-muted/60 p-3'>
                                    <A4Preview>
                                        <PrescriptionSheet
                                            prescription={sample.prescription.advice ? sample.prescription : { ...sample.prescription, advice: settings.defaultAdvice }}
                                            patient={sample.patient}
                                            consultation={sample.consultation}
                                            settings={settings}
                                            today={today}
                                        />
                                    </A4Preview>
                                </div>
                            </aside>
                        </div>
                    </TabsContent>

                    <TabsContent value='lists'>
                        <div className='grid gap-6 lg:grid-cols-3'>
                            <Panel title='Dental complaints' description='Quick picks in the consultation form' icon={ListChecks} contentClassName='p-4'>
                                <ListEditor
                                    id='settings-add-complaint'
                                    items={settings.complaints}
                                    onChange={items => update('complaints', items)}
                                    defaults={DEFAULT_COMPLAINTS}
                                    placeholder='Add a complaint'
                                    addLabel='Add a complaint'
                                />
                            </Panel>

                            <Panel title='Treatments' description='Quick picks in the consultation form' icon={ListChecks} contentClassName='p-4'>
                                <ListEditor
                                    id='settings-add-treatment'
                                    items={settings.treatments}
                                    onChange={items => update('treatments', items)}
                                    defaults={DEFAULT_TREATMENTS}
                                    placeholder='Add a treatment'
                                    addLabel='Add a treatment'
                                />
                            </Panel>

                            <Panel title='Medicine instructions' description='Suggestions in the prescription form' icon={ListChecks} contentClassName='p-4'>
                                <ListEditor
                                    id='settings-add-instruction'
                                    items={settings.medicineInstructions}
                                    onChange={items => update('medicineInstructions', items)}
                                    defaults={DEFAULT_MEDICINE_INSTRUCTIONS}
                                    placeholder='Add an instruction'
                                    addLabel='Add a medicine instruction'
                                />
                            </Panel>
                        </div>
                    </TabsContent>

                    <TabsContent value='whatsapp' className='flex flex-col gap-6'>
                        <Panel title='WhatsApp' description='Used for click-to-chat reminders' icon={MessageCircle} contentClassName='p-5'>
                            <Field
                                label='Default country code'
                                htmlFor='settings-country-code'
                                hint='Added to 10-digit phone numbers when opening WhatsApp. 91 is India.'
                                error={errors.countryCode}
                            >
                                <div className='relative max-w-[160px]'>
                                    <span className='pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground'>
                                        +
                                    </span>

                                    <Input
                                        id='settings-country-code'
                                        inputMode='numeric'
                                        value={settings.countryCode}
                                        onChange={event => update('countryCode', event.target.value.replace(/[^\d]/g, ''))}
                                        className='pl-6'
                                        aria-invalid={Boolean(errors.countryCode) || undefined}
                                    />
                                </div>
                            </Field>
                        </Panel>

                        <div className='flex flex-col gap-2'>
                            <h2 className='font-heading text-base font-semibold text-foreground'>
                                Message templates
                            </h2>

                            <p className='text-sm text-muted-foreground'>
                                Words in square brackets are replaced with the patient and clinic details when you send a reminder.
                            </p>
                        </div>

                        <TemplateEditor
                            templates={settings.templates}
                            defaultTemplateId={settings.defaultTemplateId}
                            onChange={(templates, defaultTemplateId) => {
                                setDraft(current => ({ ...(current ?? data.settings), templates, defaultTemplateId }))

                                setErrors(current => {
                                    const next = { ...current }

                                    templates.forEach(template => {
                                        if (template.name.trim()) delete next[`${template.id}-name`]
                                        if (template.body.trim()) delete next[`${template.id}-body`]
                                    })

                                    return next
                                })
                            }}
                            sampleValues={sampleValues}
                            errors={errors}
                        />
                    </TabsContent>

                    <TabsContent value='data'>
                        <Panel title='Records on this device' icon={Database} contentClassName='flex flex-col gap-5 p-5'>
                            <p className='flex items-start gap-2.5 rounded-xl bg-info-soft px-4 py-3 text-sm text-foreground'>
                                <HardDrive className='mt-0.5 size-4 shrink-0 text-info' />
                                All patient records are saved in this browser on this device and stay after a refresh. Clearing browser data removes them, and they do not appear on other devices.
                            </p>

                            <dl className='grid grid-cols-2 gap-3 sm:grid-cols-4'>
                                {counts.map(item => (
                                    <div key={item.label} className='rounded-xl border border-border bg-muted/30 px-4 py-3'>
                                        <dt className='text-xs text-muted-foreground'>{item.label}</dt>
                                        <dd className='text-xl font-bold text-foreground tabular-nums'>{item.value}</dd>
                                    </div>
                                ))}
                            </dl>

                            <p className='text-xs text-muted-foreground'>
                                Dates are shown as DD-MM-YYYY and times in 12-hour format, using this device&apos;s time zone ({timeZone}).
                            </p>

                            <div className='flex flex-col gap-3 border-t border-border pt-5 sm:flex-row'>
                                <Button
                                    variant='outline'
                                    onClick={async () => {
                                        const confirmed = await ui.confirm({
                                            title: 'Restore the sample data?',
                                            description: 'All current patients, consultations, prescriptions and follow-ups are replaced with the sample records. Your settings are kept.',
                                            confirmLabel: 'Restore sample data',
                                            destructive: true
                                        })

                                        if (!confirmed) return

                                        actions.resetToSampleData()
                                        toast.success('Sample data restored')
                                    }}
                                >
                                    <RotateCcw />
                                    Restore sample data
                                </Button>

                                <Button
                                    variant='destructive'
                                    onClick={async () => {
                                        const confirmed = await ui.confirm({
                                            title: 'Remove all records?',
                                            description: `This permanently deletes ${pluralize(data.patients.length, 'patient')} and all their consultations, prescriptions and follow-ups so you can start fresh. Your settings are kept.`,
                                            confirmLabel: 'Remove all records',
                                            destructive: true
                                        })

                                        if (!confirmed) return

                                        actions.clearAllRecords()
                                        toast.success('All records removed. Patient IDs start again from DSC-0001.')
                                    }}
                                >
                                    <Trash2 />
                                    Remove all records
                                </Button>
                            </div>
                        </Panel>
                    </TabsContent>
                </Tabs>
            </PageContainer>

            {dirty && (
                <div className='fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur-md lg:left-64'>
                    <div className='mx-auto flex max-w-7xl flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8'>
                        <p className='text-sm font-medium text-foreground'>
                            You have unsaved changes.
                        </p>

                        <div className='flex gap-2'>
                            <Button variant='ghost' onClick={discard}>
                                Discard
                            </Button>

                            <Button size='lg' onClick={save}>
                                <Save />
                                Save changes
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default SettingsPage
